import { ArrowDownTrayIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function EnrichmentTool() {
  return (
    <div>
      <div>
        <h5>
          <img src="/static/images/icons/iconsPink/dna.svg" alt="R package" className="inline w-8 h-8" />
          <img
            src="/static/images/icons/iconsPink/test-tube.svg"
            alt="AIDA"
            className="inline w-6 h-6 mr-2"
          />
          Gene & Variant-Disease Enrichment Analysis
        </h5>
        <p>
          Enrichment analysis identifies diseases that are statistically over-represented among a given list
          of genes or variants, relative to a defined background (universe).
        </p>
        <ul>
          <li className="bullet-item">
            For a <strong>gene list</strong>, the analysis evaluates which diseases are associated with the
            input genes.
          </li>
          <li className="bullet-item">
            For a <strong>variant list</strong>, the analysis evaluates which diseases are associated with the
            input variants.
          </li>
        </ul>
        <p>
          In both cases, the analysis compares the observed overlap between the input list and
          disease-associated entities with the overlap expected by chance, given the selected background
          (universe).
        </p>
      </div>

      <div className="divider"></div>
      <div className="mt-4">
        <h5 className="row">
          <img
            src="/static/images/icons/iconsPink/calculator.svg"
            alt="calculator"
            className="w-6 h-6 mr-2"
          />
          How the results are calculated
        </h5>
        <p>Each row in the results corresponds to a disease and includes:</p>
        <ul>
          <li className="bullet-item">
            <strong>Intersection:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Number of input entities (genes or variants) associated with the disease; i.e., the overlap
            between the input list and the entities associated with the disease.
          </p>
          <li className="bullet-item">
            <strong>Ratio (geneRatio / variantRatio):</strong>
          </li>
          <p className="ml-6">
            {' '}
            Proportion of input entities associated with the disease:
            <br />
            number of overlapping entities / total number of input entities
          </p>
          <li className="bullet-item">
            <strong>bgRatio:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Proportion of entities associated with the disease in the background (universe):
            <br />
            number of entities associated with the disease / total number of entities in the background
          </p>
          <li className="bullet-item">
            <strong>p-value:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Statistical significance calculated using a hypergeometric test. Lower values indicate stronger
            evidence of enrichment.
          </p>
          <li className="bullet-item">
            <strong>−log10(p-value):</strong>
          </li>
          <p className="ml-6">
            {' '}
            Negative base-10 logarithm of the p-value. Higher values correspond to smaller p-values.
          </p>
          <li className="bullet-item">
            <strong>FDR Q value:</strong>
          </li>
          <p className="ml-6">
            {' '}
            P-value adjusted for multiple hypothesis testing using False Discovery Rate correction. Lower
            values indicate stronger evidence of enrichment after correction.
          </p>
          <li className="bullet-item">
            <strong>Odds Ratio:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Measure of the degree of over-representation of a disease in the input list compared with the
            background.
            <div className="flex flex-col">
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                OR &gt; 1: over-representation (enrichment)
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                OR &lt; 1: under-representation (depletion)
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                OR ≈ 1: no enrichment or depletion
              </span>
            </div>
          </p>
          <li className="bullet-item">
            <strong>Confidence interval:</strong>
          </li>
          <p className="ml-6">
            {' '}
            Range of values that quantifies the uncertainty around the estimated odds ratio.
            <div className="flex flex-col">
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Narrow intervals indicate greater precision.
              </span>
              <span>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                If the confidence interval does not include 1, the odds ratio is statistically distinguishable
                from 1 at the corresponding confidence level.
              </span>
            </div>
          </p>
        </ul>
      </div>
      <div className="divider"></div>
      <div className="mt-4">
        <h5 className="row">
          <ArrowDownTrayIcon className="w-5 h-5 mr-2 text-pink" />
          Input requirements
        </h5>
        <ul>
          <li className="bullet-item">
            Provide at least <strong>one identifier</strong> (gene or variant)
          </li>
          <li className="bullet-item">
            Supported gene formats:
            <ul>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                NCBI Gene IDs
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Ensembl IDs
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                HGNC symbols
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                UniProt IDs
              </li>
            </ul>
          </li>
          <li className="bullet-item">
            Limits:
            <ul>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Up to <strong>4000 input items per query</strong>
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 inline-block mr-1 text-pink" />
                Trial accounts: up to <strong>10 items</strong>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
}
